import React, { useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  VStack,
  Text,
  Box,
} from "@chakra-ui/react";
import { useCreateNudgeTemplate, useUpdateNudgeTemplate } from "../hooks";
import { NudgeTemplate } from "../types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  template: NudgeTemplate | null;
}

export default function NudgeTemplateModal({ isOpen, onClose, template }: Props) {
  const createTemplate = useCreateNudgeTemplate();
  const updateTemplate = useUpdateNudgeTemplate();

  const [formData, setFormData] = React.useState({
    name: "",
    nudgeType: "milestone_reminder" as NudgeTemplate["nudgeType"],
    description: "",
    active: true,
    rules: {},
    messageTemplates: {},
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        nudgeType: template.nudgeType,
        description: template.description || "",
        active: template.active,
        rules: template.rules,
        messageTemplates: template.messageTemplates,
      });
    } else {
      setFormData({
        name: "",
        nudgeType: "milestone_reminder",
        description: "",
        active: true,
        rules: {},
        messageTemplates: {},
      });
    }
  }, [template]);

  const handleSubmit = async () => {
    if (template) {
      await updateTemplate.mutateAsync({ id: template.id, data: formData });
    } else {
      await createTemplate.mutateAsync(formData);
    }
    onClose();
  };

  const isLoading = createTemplate.isPending || updateTemplate.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {template ? "Edit" : "Create"}
          {" "}
          Nudge Template
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Weekly Team Milestones"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Type</FormLabel>
              <Select
                value={formData.nudgeType}
                onChange={(e) => setFormData({ ...formData, nudgeType: e.target.value as any })}
              >
                <option value="weekly_milestone_digest">Weekly Milestone Digest</option>
                <option value="milestone_reminder">Milestone Reminder</option>
                <option value="day_of_prompt">Day-of Prompt</option>
                <option value="task_overdue">Task Overdue</option>
                <option value="journey_stuck">Journey Stuck</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this nudge does..."
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0">Active</FormLabel>
              <Switch
                isChecked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            </FormControl>

            <Box w="full">
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                Rules Configuration (JSON)
              </Text>
              <Textarea
                value={JSON.stringify(formData.rules, null, 2)}
                onChange={(e) => {
                  try {
                    const rules = JSON.parse(e.target.value);
                    setFormData({ ...formData, rules });
                  } catch {
                    // Invalid JSON, don't update
                  }
                }}
                fontFamily="mono"
                fontSize="sm"
                minH="120px"
              />
            </Box>

            <Box w="full">
              <Text fontSize="sm" fontWeight="bold" mb={2}>
                Message Templates (JSON)
              </Text>
              <Textarea
                value={JSON.stringify(formData.messageTemplates, null, 2)}
                onChange={(e) => {
                  try {
                    const templates = JSON.parse(e.target.value);
                    setFormData({ ...formData, messageTemplates: templates });
                  } catch {
                    // Invalid JSON, don't update
                  }
                }}
                fontFamily="mono"
                fontSize="sm"
                minH="120px"
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!formData.name}
          >
            {template ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
